/**
 * ThemeContext - Single-mode (dark). The ThemeMode union and isLight/isNormal
 * flags are kept as constant-valued exports so existing call sites compile
 * unchanged; they can be cleaned up incrementally.
 */

import React, { createContext, useContext } from 'react';

/**
 * The app runs single-mode (dark). The union is kept for backwards compatibility
 * with existing call sites that branch on themeMode; at runtime the value is
 * always 'dark' and isLight/isNormal are always false.
 */
export type ThemeMode = 'light' | 'normal' | 'dark';

/**
 * Optional per-organization brand override for primary colors. When provided,
 * `useTheme()` merges these on top of the resolved theme so that downstream
 * components consuming `theme.primary*` and `theme.onPrimary` re-skin without
 * any code changes.
 */
export interface BrandPrimaryOverride {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  /** Ink color used on top of `primary` fills. Pick light or dark based on luminance. */
  onPrimary: string;
}

interface ThemeContextValue {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  isLight: boolean;
  isNormal: boolean;
  isDark: boolean;
  brandPrimary?: BrandPrimaryOverride;
}

const noop = () => {};

const VALUE: ThemeContextValue = {
  themeMode: 'dark',
  setThemeMode: noop,
  isLight: false,
  isNormal: false,
  isDark: true,
};

export const ThemeContext = createContext<ThemeContextValue | undefined>(VALUE);

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Retained for call-site compatibility; ignored in single-mode app. */
  themeModeOverride?: ThemeMode;
  brandPrimary?: BrandPrimaryOverride;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, brandPrimary }) => {
  const value: ThemeContextValue = brandPrimary ? { ...VALUE, brandPrimary } : VALUE;
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeProvider');
  }
  return context;
};
